import { ToastProps } from "@deere/fuel-react";
import { isCancel } from "axios";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { Dispatch, ReactNode } from "react";
import JobsServiceApi from "../../../api/jobsService/jobsServiceApi";
import OrderApi from "../../../api/orders/orderApi";
import { OrdersResponse } from "../../../api/orders/orderInterfaces";
import useOrderApi from "../../../api/orders/useOrderApi";
import { ALL_ACCOUNTS, ALL_ORDERS_DOWNLOAD } from "../../../utils/constants";
import downloadFile from "../../../utils/downloadFile";
import { DOWNLOAD_NEW_JOB_ADDED } from "../../../utils/eventConstants";
import { addPendingDownloadJobs } from "../../../utils/notificationsUtils";
import { convertFromDateToUTC, convertToDateToUTC, format } from "../utils";
import {
  AllOrdersActions,
  setAllOrdersAction,
  setIsAuthenticationErrorAction,
  setIsAuthorizationErrorAction,
  setIsDownloadFailedErrorAction,
  setIsDownloadingAction,
  setIsLoadingAction,
  setIsTechnicalErrorAction,
  setTabDownloadFailedAction,
  setTabDownloadingAction,
} from "./reducers/actons";
import {
  AllOrdersRequestBody,
  AllOrdersState,
  OrderStatusCount,
} from "./types";
import { getSortField, OrderRowSortField } from "./utils";

interface DownloadConfig {
  hidePromotionColumn: boolean;
  hideDateTimeSubmittedColumn: boolean;
  showDateTimeCreatedColumn: boolean;
}

interface DownloadContext {
  dispatch: Dispatch<AllOrdersActions>;
  enqueueToast: (attrs: any) => void;
  t: TFunction;
}

interface JobsDownloadContext {
  enqueueToast: (attr: ToastProps & { message: ReactNode }) => void;
  dispatch: Dispatch<AllOrdersActions>;
}

interface ToastMessages {
  errorToastMessage: string;
  successToastMessage: string;
}

interface JobsDownloadParams {
  jobServiceURL: string;
  accountIds: string[];
  locale: string;
  accessToken: string;
  hidePromotionColumn: boolean;
  hideDateTimeSubmittedColumn: boolean;
  customFileName?: string;
}

export const isRequestCancelled = (
  error: unknown,
  signal?: AbortSignal
): boolean => {
  if (signal?.aborted || isCancel(error)) return true;
  const maybeAbort = error as { name?: string; code?: string } | null;
  return (
    maybeAbort?.name === "AbortError" || maybeAbort?.code === "ERR_CANCELED"
  );
};

export const getAllOrders = (
  isMultipleAccountsSelected: boolean,
  orderServiceUrl: string,
  state: AllOrdersState,
  accessToken: string,
  dispatch: Dispatch<AllOrdersActions>,
  orderDateFilterEnhancement: boolean = false,
  enableTabToggle: boolean = false,
  signal?: AbortSignal
) => {
  if (
    state.filters.orderStatuses?.length === 0 ||
    state.filters.orderTypes?.length === 0
  ) {
    dispatch(setIsLoadingAction(true));
    setTimeout(() => {
      dispatch(
        setAllOrdersAction({
          allOrders: [],
          totalOrders: 0,
        })
      );
      dispatch(setIsLoadingAction(false));
    }, 50);
    return;
  }

  let filteredOrderStatuses = state.filters.orderStatuses;
  if (enableTabToggle) {
    if (state.tabIndex === 0) {
      filteredOrderStatuses = state.filters.orderStatuses?.filter(
        (status) => status !== "UNSUBMITTED"
      );
    } else if (state.tabIndex === 1) {
      filteredOrderStatuses = ["UNSUBMITTED"];
    }
  }

  const accountIDs = isMultipleAccountsSelected
    ? state.selectedAccounts.map((value) => value.id)
    : [state.accountId];

  const filterDate = {
    orderCreationDateFrom: state.filters.orderCreationDateFrom?.toDate(),
    orderCreationDateTo: state.filters.orderCreationDateTo?.toDate(),
  };

  const latestFilterDate = {
    orderCreationDateFrom: undefined,
    orderCreationDateTo: undefined,
    latestOrderCreationDateFrom: format(
      convertFromDateToUTC(state.filters.orderCreationDateFrom)
    ),
    latestOrderCreationDateTo: format(
      convertToDateToUTC(state.filters.orderCreationDateTo)
    ),
  };

  const tempFilters = {
    ...state.filters,
    ...(orderDateFilterEnhancement ? latestFilterDate : filterDate),
  };
  delete tempFilters.promotion;
  delete tempFilters.accountIds;

  dispatch(setIsLoadingAction(true));
  OrderApi.searchOrders(
    orderServiceUrl,
    accessToken,
    {
      accountId: accountIDs,
      pageNumber: state.currentPage - 1,
      size: state.pageRowSize,
      sortField: getSortField(
        (state.sortModel[0]?.field as OrderRowSortField) ?? null,
        isMultipleAccountsSelected
      ),
      sortDirection: state.sortModel[0]?.sort?.toUpperCase() ?? "DESC",
      ...tempFilters,
      orderStatuses:
        filteredOrderStatuses?.length === 6 ? undefined : filteredOrderStatuses,
      orderTypes:
        state.filters.orderTypes?.length === 3
          ? undefined
          : state.filters.orderTypes,
      orderSubmittedDateFrom:
        state.filters.orderSubmittedDateFrom === undefined
          ? undefined
          : formatToLocalDateString(state.filters.orderSubmittedDateFrom),
      orderSubmittedDateTo:
        state.filters.orderSubmittedDateTo === undefined
          ? undefined
          : formatToLocalDateString(state.filters.orderSubmittedDateTo),
      ...(state.filters?.promotion && { promotionId: state.filters.promotion }),
    } as AllOrdersRequestBody,
    signal
  )
    .then((response) => {
      if (signal?.aborted) return;
      dispatch(
        setAllOrdersAction({
          allOrders: response.orders,
          totalOrders: response.totalOrders,
        })
      );
      dispatch(setIsLoadingAction(false));
    })
    .catch((error: any) => {
      if (isRequestCancelled(error, signal)) return;
      handleTechnicalError(error, dispatch);
      dispatch(setIsLoadingAction(false));
    });
};

export const orderApiGetAllOrders = (
  orderApiHook: ReturnType<typeof useOrderApi>,
  isMultipleAccountsSelected: boolean,
  orderServiceUrl: string,
  state: AllOrdersState,
  dispatch: Dispatch<AllOrdersActions>,
  orderDateFilterEnhancement: boolean = false,
  enableTabToggle: boolean = false,
  signal?: AbortSignal
) => {
  if (
    state.filters.orderStatuses?.length === 0 ||
    state.filters.orderTypes?.length === 0
  ) {
    dispatch(setIsLoadingAction(true));
    setTimeout(() => {
      dispatch(
        setAllOrdersAction({
          allOrders: [],
          totalOrders: 0,
        })
      );
      dispatch(setIsLoadingAction(false));
    }, 50);
    return;
  }

  let filteredOrderStatuses = state.filters.orderStatuses;
  if (enableTabToggle) {
    if (state.tabIndex === 0) {
      filteredOrderStatuses = state.filters.orderStatuses?.filter(
        (status) => status !== "UNSUBMITTED"
      );
    } else if (state.tabIndex === 1) {
      filteredOrderStatuses = ["UNSUBMITTED"];
    }
  }

  const accountIDs = isMultipleAccountsSelected
    ? state.selectedAccounts.map((value) => value.id)
    : [state.accountId];

  const filterDate = {
    orderCreationDateFrom: state.filters.orderCreationDateFrom?.toDate(),
    orderCreationDateTo: state.filters.orderCreationDateTo?.toDate(),
  };

  const latestFilterDate = {
    orderCreationDateFrom: undefined,
    orderCreationDateTo: undefined,
    latestOrderCreationDateFrom: format(
      convertFromDateToUTC(state.filters.orderCreationDateFrom)
    ),
    latestOrderCreationDateTo: format(
      convertToDateToUTC(state.filters.orderCreationDateTo)
    ),
  };

  const tempFilters = {
    ...state.filters,
    ...(orderDateFilterEnhancement ? latestFilterDate : filterDate),
  };
  delete tempFilters.promotion;
  delete tempFilters.accountIds;

  dispatch(setIsLoadingAction(true));
  orderApiHook
    .searchOrders(
      orderServiceUrl,
      {
        accountId: accountIDs,
        pageNumber: state.currentPage - 1,
        size: state.pageRowSize,
        sortField: getSortField(
          (state.sortModel[0]?.field as OrderRowSortField) ?? null,
          isMultipleAccountsSelected
        ),
        sortDirection: state.sortModel[0]?.sort?.toUpperCase() ?? "DESC",
        ...tempFilters,
        orderStatuses:
          filteredOrderStatuses?.length === 6
            ? undefined
            : filteredOrderStatuses,
        orderTypes:
          state.filters.orderTypes?.length === 3
            ? undefined
            : state.filters.orderTypes,
        orderSubmittedDateFrom:
          state.filters.orderSubmittedDateFrom === undefined
            ? undefined
            : formatToLocalDateString(state.filters.orderSubmittedDateFrom),
        orderSubmittedDateTo:
          state.filters.orderSubmittedDateTo === undefined
            ? undefined
            : formatToLocalDateString(state.filters.orderSubmittedDateTo),
        ...(state.filters?.promotion && {
          promotionId: state.filters.promotion,
        }),
      } as AllOrdersRequestBody,
      signal
    )
    .then((response) => {
      if (signal?.aborted) return;
      dispatch(
        setAllOrdersAction({
          allOrders: response.orders,
          totalOrders: response.totalOrders,
        })
      );
      if (signal && (signal as any).requestId !== undefined) {
        if (
          (signal as any).requestId === (signal as any).getLatestRequestId()
        ) {
          dispatch(setIsLoadingAction(false));
        }
      } else {
        dispatch(setIsLoadingAction(false));
      }
    })
    .catch((error: any) => {
      if (isRequestCancelled(error, signal)) return;
      handleTechnicalError(error, dispatch);
      if (signal && (signal as any).requestId !== undefined) {
        if (
          (signal as any).requestId === (signal as any).getLatestRequestId()
        ) {
          dispatch(setIsLoadingAction(false));
        }
      } else {
        dispatch(setIsLoadingAction(false));
      }
    });
};

const getTabForDownload = (
  state: AllOrdersState
): "allOrders" | "unsubmittedOrders" =>
  state.tabIndex === 1 ? "unsubmittedOrders" : "allOrders";

const setTabDownloadingState = (
  dispatch: Dispatch<AllOrdersActions>,
  state: AllOrdersState,
  isDownloading: boolean,
  enableTabSpecific: boolean = false
) => {
  if (enableTabSpecific) {
    const tab = getTabForDownload(state);
    dispatch(setTabDownloadingAction(tab, isDownloading));
  }
  dispatch(setIsDownloadingAction(isDownloading));
};

const setTabDownloadFailedState = (
  dispatch: Dispatch<AllOrdersActions>,
  state: AllOrdersState,
  isFailed: boolean,
  enableTabSpecific: boolean = false
) => {
  if (enableTabSpecific) {
    const tab = getTabForDownload(state);
    dispatch(setTabDownloadFailedAction(tab, isFailed));
  }
  dispatch(setIsDownloadFailedErrorAction(isFailed));
};

export const downloadAllOrdersExcel = (
  ordersServiceURL: string,
  isMultipleAccountSelected: boolean,
  state: AllOrdersState,
  locale: string,
  accessToken: string,
  downloadConfig: DownloadConfig,
  downloadContext: DownloadContext,
  enableTabSpecific: boolean = false
) => {
  const { dispatch, enqueueToast, t } = downloadContext;
  const {
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
    showDateTimeCreatedColumn,
  } = downloadConfig;

  setTabDownloadingState(dispatch, state, true, enableTabSpecific);
  enqueueToast({
    variant: "info",
    message: t("orders:download.info"),
    isCloseable: true,
  });
  OrderApi.downloadOrders(
    ordersServiceURL,
    accessToken,
    isMultipleAccountSelected
      ? state.selectedAccounts.map((acc) => acc.id)
      : [state.accountId],
    locale,
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
    showDateTimeCreatedColumn
  )
    .then(downloadFile)
    .catch(() => {
      enqueueToast({
        variant: "error",
        message: t("orders:download.error"),
        isCloseable: true,
      });
      setTabDownloadFailedState(dispatch, state, true, enableTabSpecific);
    })
    .finally(() =>
      setTabDownloadingState(dispatch, state, false, enableTabSpecific)
    );
};
export const orderApiDownloadAllOrdersExcel = (
  orderApiHook: ReturnType<typeof useOrderApi>,
  ordersServiceURL: string,
  isMultipleAccountSelected: boolean,
  state: AllOrdersState,
  locale: string,
  downloadConfig: DownloadConfig,
  downloadContext: DownloadContext,
  enableTabSpecific: boolean = false
) => {
  const { dispatch, enqueueToast, t } = downloadContext;
  const {
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
    showDateTimeCreatedColumn,
  } = downloadConfig;

  setTabDownloadingState(dispatch, state, true, enableTabSpecific);
  enqueueToast({
    variant: "info",
    message: t("orders:download.info"),
    isCloseable: true,
  });
  orderApiHook
    .downloadOrders(
      ordersServiceURL,
      isMultipleAccountSelected
        ? state.selectedAccounts.map((acc) => acc.id)
        : [state.accountId],
      locale,
      hidePromotionColumn,
      hideDateTimeSubmittedColumn,
      showDateTimeCreatedColumn
    )
    .then(downloadFile)
    .catch(() => {
      enqueueToast({
        variant: "error",
        message: t("orders:download.error"),
        isCloseable: true,
      });
      setTabDownloadFailedState(dispatch, state, true, enableTabSpecific);
    })
    .finally(() =>
      setTabDownloadingState(dispatch, state, false, enableTabSpecific)
    );
};
export const invokeAllOrdersExcelDownload = (
  jobsParams: JobsDownloadParams,
  state: AllOrdersState,
  toastMessages: ToastMessages,
  jobsDownloadContext: JobsDownloadContext,
  enableTabSpecific: boolean = false
) => {
  const { dispatch, enqueueToast } = jobsDownloadContext;
  const { errorToastMessage, successToastMessage } = toastMessages;
  const {
    jobServiceURL,
    accountIds,
    locale,
    accessToken,
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
  } = jobsParams;

  setTabDownloadingState(dispatch, state, true, enableTabSpecific);
  JobsServiceApi.invokeAllOrdersExcelDownload(
    jobServiceURL,
    {
      accountIds,
      locale,
      hidePromotionColumn,
      hideDateTimeSubmittedColumn,
      showDateTimeCreatedColumn: hideDateTimeSubmittedColumn,
      sortField:
        getSortField(
          (state.sortModel[0]?.field as OrderRowSortField) ?? null,
          accountIds.length > 1
        ) || "",
      sortDirection: (state.sortModel[0]?.sort?.toUpperCase() ?? "DESC") as
        | "DESC"
        | "ASC",
    },
    accessToken
  )
    .then((response: { jobId: string }) => {
      const accountId = accountIds.length > 1 ? ALL_ACCOUNTS : accountIds[0];
      addPendingDownloadJobs(accountId, [
        {
          ...response,
          createdAt: Date.now(),
          accountId,
          type: ALL_ORDERS_DOWNLOAD,
        },
      ]);
      enqueueToast({
        variant: "info",
        message: successToastMessage,
        isCloseable: true,
      });
      window.dispatchEvent(
        new CustomEvent(DOWNLOAD_NEW_JOB_ADDED, { bubbles: true })
      );
    })
    .catch(() => {
      enqueueToast({
        variant: "error",
        message: errorToastMessage,
        isCloseable: true,
      });
      setTabDownloadingState(dispatch, state, false, enableTabSpecific);
    });
};

const convertFiltersToApiParams = (
  filters: any,
  orderStatuses: any
): Record<string, any> => {
  const apiParams: Record<string, any> = {};

  if (filters.orderNumber) {
    apiParams.orderNumber = filters.orderNumber;
  }
  if (filters.dealerReference) {
    apiParams.dealerReference = filters.dealerReference;
  }
  if (filters.promotion) {
    apiParams.promotionId = filters.promotion;
  }
  if (filters.orderTypes && filters.orderTypes.length > 0) {
    apiParams.orderTypes = filters.orderTypes;
  }
  if (filters.orderStatuses && filters.orderStatuses.length > 0) {
    apiParams.orderStatuses = orderStatuses;
  }
  if (filters.orderCreationDateFrom) {
    apiParams.orderCreationDateFrom = format(
      convertFromDateToUTC(filters.orderCreationDateFrom)
    );
  }
  if (filters.orderCreationDateTo) {
    apiParams.orderCreationDateTo = format(
      convertToDateToUTC(filters.orderCreationDateTo)
    );
  }
  if (filters.orderSubmittedDateFrom) {
    apiParams.orderSubmittedDateFrom = filters.orderSubmittedDateFrom;
  }
  if (filters.orderSubmittedDateTo) {
    apiParams.orderSubmittedDateTo = filters.orderSubmittedDateTo;
  }
  return apiParams;
};
export const invokeAllOrdersExcelDownloadWithFilters = (
  jobsParams: JobsDownloadParams,
  state: AllOrdersState,
  toastMessages: ToastMessages,
  jobsDownloadContext: JobsDownloadContext,
  enableTabSpecific: boolean = false
) => {
  const { dispatch, enqueueToast } = jobsDownloadContext;
  const { errorToastMessage, successToastMessage } = toastMessages;
  const {
    jobServiceURL,
    accountIds,
    locale,
    accessToken,
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
    customFileName,
  } = jobsParams;

  setTabDownloadingState(dispatch, state, true, enableTabSpecific);
  let orderStatuses;
  if (state.tabIndex === 0) {
    orderStatuses = state.filters.orderStatuses?.filter(
      (status) => status !== "UNSUBMITTED"
    );
  } else if (state.tabIndex === 1) {
    orderStatuses = ["UNSUBMITTED"];
  }

  const filters = convertFiltersToApiParams(state.filters, orderStatuses);

  const request = {
    accountIds,
    locale,
    hidePromotionColumn,
    hideDateTimeSubmittedColumn,
    showDateTimeCreatedColumn: hideDateTimeSubmittedColumn,
    filters,
    sortField:
      getSortField(
        (state.sortModel[0]?.field as OrderRowSortField) ?? null,
        accountIds.length > 1
      ) || "",
    sortDirection: (state.sortModel[0]?.sort?.toUpperCase() ?? "DESC") as
      | "DESC"
      | "ASC",
  };

  JobsServiceApi.invokeAllOrdersExcelDownloadWithFilters(
    jobServiceURL,
    request,
    accessToken
  )
    .then((response: { jobId: string }) => {
      const accountId = accountIds.length > 1 ? ALL_ACCOUNTS : accountIds[0];
      const fileName =
        customFileName || orderDownloadFileName(state, enableTabSpecific);
      const displayName = orderDownloadDisplayName(state, enableTabSpecific);
      addPendingDownloadJobs(accountId, [
        {
          ...response,
          createdAt: Date.now(),
          accountId,
          type: ALL_ORDERS_DOWNLOAD,
          fileName,
          displayName,
        },
      ]);
      enqueueToast({
        variant: "info",
        message: successToastMessage,
        isCloseable: true,
      });
      window.dispatchEvent(
        new CustomEvent(DOWNLOAD_NEW_JOB_ADDED, { bubbles: true })
      );
    })
    .catch(() => {
      enqueueToast({
        variant: "error",
        message: errorToastMessage,
        isCloseable: true,
      });
      setTabDownloadingState(dispatch, state, false, enableTabSpecific);
    });
};

export const getTooltipTextForDownloadButton = (
  allOrders: OrdersResponse[],
  isFilterApplied: boolean,
  isDownloading: boolean,
  t: TFunction
) => {
  if (allOrders.length === 0 && !isFilterApplied) return "";
  if (isDownloading) return t("orders:download.disabled.toolTip");
  return t("orders:download.toolTip");
};

const handleTechnicalError = (
  error: any,
  dispatch: Dispatch<AllOrdersActions>
) => {
  if (
    error?.response?.data?.statusCode === 401 ||
    error?.response?.status === 401
  )
    dispatch(setIsAuthenticationErrorAction(true));
  else if (
    error?.response?.data?.statusCode === 403 ||
    error?.response?.status === 403
  )
    dispatch(setIsAuthorizationErrorAction(true));
  else dispatch(setIsTechnicalErrorAction(true));
};
const formatToLocalDateString = (date: Date | undefined): string | undefined =>
  date ? dayjs(date).format("YYYY-MM-DD") : undefined;

export const buildOrdersRequestBody = (
  currentState: AllOrdersState,
  multipleAccountsSelected: boolean,
  orderDateFilterEnhancement: boolean,
  tabIndex: number
): AllOrdersRequestBody => {
  let filteredOrderStatuses = currentState.filters.orderStatuses;
  if (tabIndex === 0) {
    filteredOrderStatuses = currentState.filters.orderStatuses?.filter(
      (status) => status !== "UNSUBMITTED"
    );
  } else if (tabIndex === 1) {
    filteredOrderStatuses = ["UNSUBMITTED"];
  }

  const accountIDs = multipleAccountsSelected
    ? currentState.selectedAccounts.map((value) => value.id)
    : [currentState.accountId];

  const filterDate = {
    orderCreationDateFrom: currentState.filters.orderCreationDateFrom?.toDate(),
    orderCreationDateTo: currentState.filters.orderCreationDateTo?.toDate(),
  };

  const latestFilterDate = {
    orderCreationDateFrom: undefined,
    orderCreationDateTo: undefined,
    latestOrderCreationDateFrom: format(
      convertFromDateToUTC(currentState.filters.orderCreationDateFrom)
    ),
    latestOrderCreationDateTo: format(
      convertToDateToUTC(currentState.filters.orderCreationDateTo)
    ),
  };

  const tempFilters = {
    ...currentState.filters,
    ...(orderDateFilterEnhancement ? latestFilterDate : filterDate),
  };
  delete tempFilters.promotion;
  delete tempFilters.accountIds;

  return {
    accountId: accountIDs,
    pageNumber: currentState.currentPage - 1,
    size: currentState.pageRowSize,
    sortField: getSortField(
      (currentState.sortModel[0]?.field as OrderRowSortField) ?? null,
      multipleAccountsSelected
    ),
    sortDirection: currentState.sortModel[0]?.sort?.toUpperCase() ?? "DESC",
    ...tempFilters,
    orderStatuses:
      filteredOrderStatuses?.length === 6 ? undefined : filteredOrderStatuses,
    orderTypes:
      currentState.filters.orderTypes?.length === 3
        ? undefined
        : currentState.filters.orderTypes,
    orderSubmittedDateFrom:
      currentState.filters.orderSubmittedDateFrom === undefined
        ? undefined
        : formatToLocalDateString(currentState.filters.orderSubmittedDateFrom),
    orderSubmittedDateTo:
      currentState.filters.orderSubmittedDateTo === undefined
        ? undefined
        : formatToLocalDateString(currentState.filters.orderSubmittedDateTo),
    ...(currentState.filters?.promotion && {
      promotionId: currentState.filters.promotion,
    }),
  } as AllOrdersRequestBody;
};

const getOrderTypesForFiltering = (
  orderTypes: string[] | undefined
): string[] | undefined => {
  if (orderTypes?.length === 0) {
    return [];
  }
  if (orderTypes?.length === 3) {
    return undefined;
  }
  return orderTypes;
};

const getOrderStatusesForAllOrders = (
  orderStatuses: string[] | undefined | null
): string[] => {
  if (orderStatuses === undefined || orderStatuses === null) {
    return ["SUBMITTED", "CANCELLED", "COMPLETED", "ON_HOLD", "IN_PROCESS"];
  }
  if (orderStatuses.length === 0) {
    return [];
  }
  return orderStatuses.filter((status) => status !== "UNSUBMITTED");
};

const createBaseRequestBody = (
  isMultipleAccountsSelected: boolean,
  state: AllOrdersState,
  orderDateFilterEnhancement: boolean
): AllOrdersRequestBody => {
  const accountIDs = isMultipleAccountsSelected
    ? state.selectedAccounts.map((value) => value.id)
    : [state.accountId];

  const filterDate = {
    orderCreationDateFrom: state.filters.orderCreationDateFrom?.toDate(),
    orderCreationDateTo: state.filters.orderCreationDateTo?.toDate(),
  };

  const latestFilterDate = {
    orderCreationDateFrom: undefined,
    orderCreationDateTo: undefined,
    latestOrderCreationDateFrom: format(
      convertFromDateToUTC(state.filters.orderCreationDateFrom)
    ),
    latestOrderCreationDateTo: format(
      convertToDateToUTC(state.filters.orderCreationDateTo)
    ),
  };

  const tempFilters = {
    ...state.filters,
    ...(orderDateFilterEnhancement ? latestFilterDate : filterDate),
  };
  delete tempFilters.promotion;
  delete tempFilters.accountIds;

  return {
    accountId: accountIDs,
    pageNumber: 0,
    size: 1,
    sortField: getSortField(
      (state.sortModel[0]?.field as OrderRowSortField) ?? null,
      isMultipleAccountsSelected
    ),
    sortDirection: state.sortModel[0]?.sort?.toUpperCase() ?? "DESC",
    ...tempFilters,
    orderTypes: getOrderTypesForFiltering(state.filters.orderTypes),
    orderSubmittedDateFrom:
      state.filters.orderSubmittedDateFrom === undefined
        ? undefined
        : formatToLocalDateString(state.filters.orderSubmittedDateFrom),
    orderSubmittedDateTo:
      state.filters.orderSubmittedDateTo === undefined
        ? undefined
        : formatToLocalDateString(state.filters.orderSubmittedDateTo),
    ...(state.filters?.promotion && { promotionId: state.filters.promotion }),
  } as AllOrdersRequestBody;
};

const handleAllOrdersTab = async (
  orderServiceUrl: string,
  accessToken: string,
  baseRequestBody: AllOrdersRequestBody,
  state: AllOrdersState
): Promise<OrderStatusCount> => {
  const orderStatusesForAllOrders = getOrderStatusesForAllOrders(
    state.filters.orderStatuses
  );

  if (orderStatusesForAllOrders.length === 0) {
    return {
      allOrders: 0,
      unsubmittedOrders: state.statusCount.unsubmittedOrders,
    };
  }

  const allOrdersResponse = await OrderApi.searchOrders(
    orderServiceUrl,
    accessToken,
    {
      ...baseRequestBody,
      orderStatuses: orderStatusesForAllOrders,
    }
  );

  return {
    allOrders: allOrdersResponse.totalOrders,
    unsubmittedOrders: state.statusCount.unsubmittedOrders,
  };
};

const handleAllOrdersTabForOrderApi = async (
  orderApiHook: ReturnType<typeof useOrderApi>,
  orderServiceUrl: string,
  baseRequestBody: AllOrdersRequestBody,
  state: AllOrdersState
): Promise<OrderStatusCount> => {
  const orderStatusesForAllOrders = getOrderStatusesForAllOrders(
    state.filters.orderStatuses
  );

  if (orderStatusesForAllOrders.length === 0) {
    return {
      allOrders: 0,
      unsubmittedOrders: state.statusCount.unsubmittedOrders,
    };
  }

  const allOrdersResponse = await orderApiHook.searchOrders(orderServiceUrl, {
    ...baseRequestBody,
    orderStatuses: orderStatusesForAllOrders,
  });

  return {
    allOrders: allOrdersResponse.totalOrders,
    unsubmittedOrders: state.statusCount.unsubmittedOrders,
  };
};

export const fetchStatusCounts = async (
  isMultipleAccountsSelected: boolean,
  orderServiceUrl: string,
  state: AllOrdersState,
  accessToken: string,
  orderDateFilterEnhancement: boolean = false,
  activeTabIndex: number = 0
): Promise<OrderStatusCount> => {
  try {
    const baseRequestBody = createBaseRequestBody(
      isMultipleAccountsSelected,
      state,
      orderDateFilterEnhancement
    );

    if (activeTabIndex === 0) {
      return await handleAllOrdersTab(
        orderServiceUrl,
        accessToken,
        baseRequestBody,
        state
      );
    }

    const unsubmittedResponse = await OrderApi.searchOrders(
      orderServiceUrl,
      accessToken,
      {
        ...baseRequestBody,
        orderStatuses: ["UNSUBMITTED"],
      }
    );

    return {
      allOrders: state.statusCount.allOrders,
      unsubmittedOrders: unsubmittedResponse.totalOrders,
    };
  } catch (error) {
    console.error("Error fetching status counts:", error);
    return {
      allOrders: 0,
      unsubmittedOrders: 0,
    };
  }
};

export const orderApiFetchStatusCounts = async (
  orderApiHook: ReturnType<typeof useOrderApi>,
  isMultipleAccountsSelected: boolean,
  orderServiceUrl: string,
  state: AllOrdersState,
  orderDateFilterEnhancement: boolean = false,
  activeTabIndex: number = 0
): Promise<OrderStatusCount> => {
  try {
    const baseRequestBody = createBaseRequestBody(
      isMultipleAccountsSelected,
      state,
      orderDateFilterEnhancement
    );

    if (activeTabIndex === 0) {
      return await handleAllOrdersTabForOrderApi(
        orderApiHook,
        orderServiceUrl,
        baseRequestBody,
        state
      );
    }

    const unsubmittedResponse = await orderApiHook.searchOrders(
      orderServiceUrl,
      {
        ...baseRequestBody,
        orderStatuses: ["UNSUBMITTED"],
      }
    );

    return {
      allOrders: state.statusCount.allOrders,
      unsubmittedOrders: unsubmittedResponse.totalOrders,
    };
  } catch (error) {
    console.error("Error fetching status counts:", error);
    return {
      allOrders: 0,
      unsubmittedOrders: 0,
    };
  }
};

export const orderDownloadFileName = (
  state: AllOrdersState,
  isTabSpecificDownloadEnabled: boolean
) => {
  const yyMMddHHmm = dayjs().format("DDMMYYHHmmss");
  if (isTabSpecificDownloadEnabled) {
    if (state.tabIndex === 0) {
      return `All_Orders_${state.accountId}_${yyMMddHHmm}`;
    }
    if (state.tabIndex === 1) {
      return `Unsubmitted_Orders_${state.accountId}_${yyMMddHHmm}`;
    }
  }
  return "All_Orders";
};

export const orderDownloadDisplayName = (
  state: AllOrdersState,
  isTabSpecificDownloadEnabled: boolean
) => {
  if (isTabSpecificDownloadEnabled) {
    if (state.tabIndex === 0) {
      return "All Orders";
    }
    if (state.tabIndex === 1) {
      return "Unsubmitted Orders";
    }
  }
  return "All Orders";
};

export const getOrderNotificationNames = (
  isTabSpecificDownloadEnabled: boolean,
  filename?: string,
  displayName?: string,
  accountId?: string,
  createdAt?: Date | number
) => {
  const isUnsubmittedOrders =
    filename?.includes("Unsubmitted") ||
    displayName?.includes("Unsubmitted") ||
    filename?.includes("Unsubmitted_Orders") ||
    displayName?.includes("Unsubmitted Orders");

  const mockState = {
    accountId: accountId || "",
    tabIndex: isUnsubmittedOrders ? 1 : 0,
  } as AllOrdersState;

  const properDisplayName = orderDownloadDisplayName(
    mockState,
    isTabSpecificDownloadEnabled
  );
  let properFileName: string;
  if (createdAt && isTabSpecificDownloadEnabled) {
    const timestamp =
      typeof createdAt === "number" ? new Date(createdAt) : createdAt;
    const yyMMddHHmm = dayjs(timestamp).format("DDMMYYHHmmss");

    if (mockState.tabIndex === 0) {
      properFileName = `All_Orders_${mockState.accountId}_${yyMMddHHmm}`;
    } else if (mockState.tabIndex === 1) {
      properFileName = `Unsubmitted_Orders_${mockState.accountId}_${yyMMddHHmm}`;
    } else {
      properFileName = orderDownloadFileName(
        mockState,
        isTabSpecificDownloadEnabled
      );
    }
  } else {
    properFileName = orderDownloadFileName(
      mockState,
      isTabSpecificDownloadEnabled
    );
  }

  return {
    displayName: properDisplayName,
    fileName: properFileName,
  };
};
